import { useEffect, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useUnreadMessages } from '../contexts/UnreadMessagesContext';
import { useMessageNotifications } from '../contexts/MessageNotificationsContext';
import { useToastNotifications } from '../contexts/ToastNotificationsContext';
import { useActiveConversation } from '../contexts/ActiveConversationContext';
import useAuth from './useAuth';
import { getUrl } from '../config';

const useGlobalMessaging = () => {
    const { user } = useAuth();
    const { addUnreadMessage, setAllUnreadMessages } = useUnreadMessages();
    const { addMessageNotification } = useMessageNotifications();
    const { addToastNotification } = useToastNotifications();
    const { activeConversationId } = useActiveConversation();
    const [socket, setSocket] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [allUsers, setAllUsers] = useState([]);
    const [usersLoaded, setUsersLoaded] = useState(false);
    const [hasLoadedMessages, setHasLoadedMessages] = useState(false);
    const [userConversations, setUserConversations] = useState([]);

    // Récupérer tous les utilisateurs pour les notifications
    const fetchAllUsers = useCallback(async () => {
        try {
            const response = await fetch(`${getUrl()}/api/getAllUsers`);
            if (!response.ok) return;

            const data = await response.json();
            if (data && data.status === 'success') {
                setAllUsers(data.data);
                setUsersLoaded(true);
            }
        } catch (error) {
            // Erreur silencieuse pour éviter le spam
        }
    }, []);

    // Charger les messages non lus et créer des notifications
    const loadUnreadMessages = useCallback(async () => {
        if (!user || !usersLoaded || hasLoadedMessages) return;

        try {
            const response = await fetch(`${getUrl()}/api/conversations/user?userId=${user.id}`);
            if (!response.ok) return;

            const data = await response.json();
            if (data && data.status === 'success') {
                const conversations = data.data;

                // Sauvegarder les conversations de l'utilisateur pour la vérification des notifications
                setUserConversations(conversations);

                const unreadCounts = {};

                for (const conversation of conversations) {
                    try {
                        const token = localStorage.getItem('accessToken');
                        const headers = { 'Content-Type': 'application/json' };
                        if (token) headers['Authorization'] = `Bearer ${token}`;

                        const messagesResponse = await fetch(`${getUrl()}/api/messages/conversation/${conversation.id}`, { headers });

                        if (messagesResponse.ok) {
                            const messagesData = await messagesResponse.json();
                            if (messagesData && messagesData.status === 'success') {
                                const messages = messagesData.data;

                                const lastSeenKey = `lastSeen_${user.id}_${conversation.id}`;
                                const lastSeen = localStorage.getItem(lastSeenKey);
                                const lastSeenDate = lastSeen ? new Date(lastSeen) : new Date(0);

                                const unreadMessages = messages.filter(msg => {
                                    const isNotMine = String(msg.userId) !== String(user.id);
                                    const isAfterLastSeen = new Date(msg.createdAt || msg.timestamp) > lastSeenDate;
                                    return isNotMine && isAfterLastSeen;
                                });

                                if (unreadMessages.length > 0) {
                                    unreadCounts[conversation.id] = unreadMessages.length;

                                    // Créer une notification pour le dernier message non lu
                                    const latestUnreadMessage = unreadMessages[unreadMessages.length - 1];

                                    if (latestUnreadMessage) {
                                        const senderUser = allUsers.find(u =>
                                            u.id === latestUnreadMessage.userId ||
                                            u.id === parseInt(latestUnreadMessage.userId)
                                        );

                                        const senderInfo = {
                                            username: senderUser?.username || latestUnreadMessage.username || 'Utilisateur',
                                            erpName: senderUser?.erpName || 'ERP'
                                        };

                                        addMessageNotification(latestUnreadMessage, senderInfo);
                                    }
                                }
                            }
                        }
                    } catch (error) {
                        // Erreur silencieuse
                    }
                }

                if (Object.keys(unreadCounts).length > 0) {
                    setAllUnreadMessages(unreadCounts);
                }

                // Marquer comme chargé pour éviter les rechargements
                setHasLoadedMessages(true);
            }
        } catch (error) {
            setHasLoadedMessages(true); // Marquer comme tenté même en cas d'erreur
        }
    }, [user, usersLoaded, hasLoadedMessages, allUsers, setAllUnreadMessages, addMessageNotification, setUserConversations]);

    // Gestionnaire pour les nouveaux messages (avec la dernière valeur de allUsers)
    const handleNewMessage = useCallback((message) => {
        // Vérifier que le message est valide
        if (!message || String(message.userId) === String(user.id)) {
            return;
        }

        // Si l'utilisateur est actuellement dans cette conversation, ne pas créer de notifications
        if (activeConversationId && String(activeConversationId) === String(message.conversationId)) {
            return;
        }

        // IMPORTANT: Vérifier que l'utilisateur actuel participe à cette conversation
        // Cela évite que tous les utilisateurs connectés reçoivent toutes les notifications
        let isParticipant = userConversations.some(conv => conv.id === message.conversationId);

        // Si la conversation n'est pas dans la liste, vérifier si c'est une nouvelle conversation
        // en récupérant les conversations mises à jour
        if (!isParticipant) {
            // Récupérer les conversations mises à jour pour détecter les nouvelles
            fetch(`${getUrl()}/api/conversations/user?userId=${user.id}`)
                .then(response => response.json())
                .then(data => {
                    if (data && data.status === 'success') {
                        const updatedConversations = data.data;
                        const conversationExists = updatedConversations.some(conv => conv.id === message.conversationId);

                        if (conversationExists) {
                            // Mettre à jour la liste des conversations
                            setUserConversations(updatedConversations);

                            // Vérifier encore une fois si l'utilisateur est dans cette conversation avant de créer les notifications
                            if (activeConversationId && String(activeConversationId) === String(message.conversationId)) {
                                return;
                            }

                            // Créer les notifications maintenant que nous savons que l'utilisateur participe

                            addUnreadMessage(message.conversationId);

                            const senderUser = allUsers.find(u => u.id === message.userId || u.id === parseInt(message.userId));

                            const senderInfo = {
                                username: senderUser?.username || message.username || 'Utilisateur',
                                erpName: senderUser?.erpName || 'ERP'
                            };

                            // Ajouter notification dans la navbar
                            addMessageNotification(message, senderInfo);
                            // Ajouter notification toast popup
                            addToastNotification(message, senderInfo);
                        }
                    }
                })
                .catch(() => {
                    // Erreur silencieuse
                });
            return; // Arrêter ici pour éviter le double traitement
        }

        addUnreadMessage(message.conversationId);

        const senderUser = allUsers.find(u => u.id === message.userId || u.id === parseInt(message.userId));

        const senderInfo = {
            username: senderUser?.username || message.username || 'Utilisateur',
            erpName: senderUser?.erpName || 'ERP'
        };

        // Ajouter notification dans la navbar
        addMessageNotification(message, senderInfo);
        // Ajouter notification toast popup
        addToastNotification(message, senderInfo);
    }, [user, allUsers, userConversations, activeConversationId, addUnreadMessage, addMessageNotification, addToastNotification, setUserConversations]);

    // Charger les utilisateurs au démarrage
    useEffect(() => {
        if (user) {
            fetchAllUsers();
        }
    }, [user, fetchAllUsers]);

    // Charger les messages non lus quand les utilisateurs sont prêts
    useEffect(() => {
        if (user && usersLoaded && !hasLoadedMessages) {
            loadUnreadMessages();
        }
    }, [user, usersLoaded, hasLoadedMessages, loadUnreadMessages]);

    // Initialisation Socket.io
    useEffect(() => {
        if (!user) return;

        const socketInstance = io(getUrl(), {
            transports: ['websocket', 'polling']
        });

        socketInstance.on('connect', () => {
            setIsConnected(true);

            // Rejoindre la room personnelle de l'utilisateur
            socketInstance.emit('user', `user-${user.id}`);

            socketInstance.emit('userConnected', {
                id: user.id,
                username: user.username,
                erpId: user.erpId
            });
        });

        socketInstance.on('connect_error', (error) => {
            setIsConnected(false);
        });

        socketInstance.on('disconnect', (reason) => {
            setIsConnected(false);
        });

        socketInstance.on('newMessage', handleNewMessage);

        // Écouter les événements de conversation démasquée
        socketInstance.on('conversationUnhidden', (data) => {
            // Si c'est pour l'utilisateur actuel, recharger les conversations
            if (data.userId === user.id) {
                console.log(`[DEBUG] Conversation ${data.conversationId} démasquée, rechargement des conversations`);
                // Recharger les conversations après un court délai
                setTimeout(() => {
                    setHasLoadedMessages(false); // Permettre le rechargement
                    loadUnreadMessages();
                }, 500);
            }
        });

        setSocket(socketInstance);

        return () => {
            socketInstance.disconnect();
        };
    }, [user, handleNewMessage]);

    return {
        socket,
        isConnected
    };
};

export default useGlobalMessaging; 